import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { aurora } from '@/components/aurora/tokens';
import {
  MessageSquare, Heart, Share2, MoreHorizontal, Pin, Edit, Trash2,
  CheckSquare, Calendar, Video, Eye, EyeOff, Megaphone, Award,
  BarChart3, Paperclip, Hash, ThumbsUp, BookmarkPlus, Bookmark as BookmarkIcon, Flag, Tag,
  Rocket, HelpCircle, Trophy, CheckCircle2, Sparkles,
} from 'lucide-react';
import type { FeedReactionKind, FeedRsvpStatus } from '@easyflow/shared';

const FEED_REACTION_KIND_EMOJI: Record<Exclude<FeedReactionKind, 'custom'>, string> = {
  like: '👍',
  love: '❤️',
  celebrate: '🎉',
  idea: '💡',
  curious: '🤔',
  thanks: '🙏',
  applause: '👏',
};
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@easyflow/ui-frontend/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@easyflow/ui-frontend/components/ui/dropdown-menu';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { cn } from '@/lib/utils';
import CommentEditor from './CommentEditor';
import PollCard from './PollCard';
import { EntityCard } from '@/components/cross/EntityCard';
import type { FeedPost, FeedComment, FeedReaction, FeedAttachment, FeedTopic, ContentJson } from '@/types/feed';
import type { LucideIcon } from 'lucide-react';

interface PostCardProps {
  post: FeedPost;
  currentUserId: string;
  isAdmin: boolean;
  /** Task #1665 — indice nella lista per stagger entry animation (primi 10). */
  index?: number;
  onReact: (postId: string, emoji: string, kind?: FeedReactionKind) => void;
  onComment: (postId: string, html: string, json: ContentJson) => void;
  onDelete: (postId: string) => void;
  onEdit: (post: FeedPost) => void;
  onPin: (postId: string, pinned: boolean) => void;
  onFollow: (postId: string, following: boolean) => void;
  onMarkRead: (postId: string) => void;
  onCreateTask: (postId: string) => void;
  onCreateAppointment: (postId: string) => void;
  onCreateVideocall: (postId: string) => void;
  onVotePoll: (pollId: string, optionId: string) => void;
  onReport?: (post: FeedPost) => void;
  comments?: FeedComment[];
  onLoadComments?: (postId: string) => void;
  // Task #1662 — Feed v2 engagement handlers (opzionali per back-compat)
  onBookmark?: (postId: string, bookmarked: boolean) => void;
  onRsvp?: (postId: string, status: FeedRsvpStatus) => void;
  onBoost?: (postId: string, hours: number) => void;
  onClearBoost?: (postId: string) => void;
  onMarkOfficial?: (postId: string, commentId: string) => void;
  onUnmarkOfficial?: (postId: string) => void;
  rsvpSummary?: { yes: number; maybe: number; no: number; my_status: FeedRsvpStatus | null } | null;
}

// Task #1662 — Reazioni tassonomiche v2: 7 kind fissi (custom riservato a future estensioni).
const QUICK_REACTIONS_V2: Array<{ kind: FeedReactionKind; emoji: string }> = [
  { kind: 'like', emoji: FEED_REACTION_KIND_EMOJI.like },
  { kind: 'love', emoji: FEED_REACTION_KIND_EMOJI.love },
  { kind: 'celebrate', emoji: FEED_REACTION_KIND_EMOJI.celebrate },
  { kind: 'idea', emoji: FEED_REACTION_KIND_EMOJI.idea },
  { kind: 'curious', emoji: FEED_REACTION_KIND_EMOJI.curious },
  { kind: 'thanks', emoji: FEED_REACTION_KIND_EMOJI.thanks },
  { kind: 'applause', emoji: FEED_REACTION_KIND_EMOJI.applause },
];

const POST_TYPE_CONFIG: Record<string, { icon: LucideIcon; label: string; bgClass: string; badgeColor: string }> = {
  announcement: { icon: Megaphone, label: 'Annuncio', bgClass: 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border-amber-200 dark:border-amber-800', badgeColor: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300' },
  event: { icon: Calendar, label: 'Evento', bgClass: 'bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950/20 dark:to-cyan-950/20 border-blue-200 dark:border-blue-800', badgeColor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' },
  poll: { icon: BarChart3, label: 'Sondaggio', bgClass: 'bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-950/20 dark:to-violet-950/20 border-purple-200 dark:border-purple-800', badgeColor: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300' },
  appreciation: { icon: Award, label: 'Apprezzamento', bgClass: 'bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20 border-pink-200 dark:border-pink-800', badgeColor: 'bg-pink-100 text-pink-700 dark:bg-pink-900/50 dark:text-pink-300' },
  file: { icon: Paperclip, label: 'File', bgClass: '', badgeColor: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  message: { icon: MessageSquare, label: 'Post', bgClass: '', badgeColor: '' },
  question: { icon: HelpCircle, label: 'Domanda', bgClass: 'bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-950/20 dark:to-blue-950/20 border-indigo-200 dark:border-indigo-800', badgeColor: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300' },
  milestone: { icon: Trophy, label: 'Milestone', bgClass: 'bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 dark:from-emerald-950/30 dark:via-teal-950/20 dark:to-cyan-950/30 border-emerald-200 dark:border-emerald-800', badgeColor: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' },
};

const AVATAR_GRADIENTS = [
  'from-blue-500 to-indigo-600', 'from-emerald-500 to-teal-600', 'from-purple-500 to-violet-600',
  'from-orange-500 to-red-600', 'from-pink-500 to-rose-600', 'from-cyan-500 to-blue-600',
  'from-amber-500 to-orange-600', 'from-indigo-500 to-purple-600',
];

function getAvatarGradient(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

function PostCard({
  post, currentUserId, isAdmin, index = 0,
  onReact, onComment, onDelete, onEdit, onPin, onFollow, onMarkRead,
  onCreateTask, onCreateAppointment, onCreateVideocall, onVotePoll,
  onReport,
  comments, onLoadComments,
  onBookmark, onRsvp, onBoost, onClearBoost, onMarkOfficial, onUnmarkOfficial,
  rsvpSummary,
}: PostCardProps) {
  const [showComments, setShowComments] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  const config = (POST_TYPE_CONFIG[post.post_type] || POST_TYPE_CONFIG.message)!;
  const isAuthor = post.author_id === currentUserId;
  const authorName = `${post.author_first_name || ''} ${post.author_last_name || ''}`.trim() || 'Utente';
  const authorInitials = `${(post.author_first_name || 'U')[0]}${(post.author_last_name || '')[0] || ''}`;

  const handleComment = async (html: string, json: ContentJson) => {
    setIsSubmittingComment(true);
    try {
      await onComment(post.id, html, json);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const toggleComments = () => {
    if (!showComments && onLoadComments) {
      onLoadComments(post.id);
    }
    setShowComments(!showComments);
  };

  const reactions: FeedReaction[] = post.reactions || [];
  const totalReactions = reactions.reduce((sum, r) => sum + (r.count || 0), 0);
  // Task #1665 — entry motion soft, disabilitata per chi preferisce reduced-motion.
  const reduceMotion = useReducedMotion();

  return (
    <motion.article
      role="article"
      aria-labelledby={`post-${post.id}-author`}
      initial={reduceMotion ? false : aurora.listItemIn.initial}
      animate={aurora.listItemIn.animate}
      transition={reduceMotion
        ? { duration: 0 }
        : { ...aurora.listItemIn.transition, delay: aurora.stagger(index) }}
      className={cn(
        "bg-white dark:bg-gray-900 rounded-xl border shadow-sm transition-all duration-200 hover:shadow-md group/card",
        config.bgClass,
        post.is_pinned && "ring-2 ring-amber-400/60 dark:ring-amber-600/60"
      )}>
      {post.is_pinned && (
        <div className="flex items-center gap-1.5 px-4 pt-3 text-xs text-amber-600 dark:text-amber-400 font-semibold">
          <Pin className="h-3 w-3 rotate-45" /> Post fissato
        </div>
      )}
      {/* Task #1662 — Boost badge */}
      {post.boost_until && new Date(post.boost_until) > new Date() && (
        <div className="flex items-center gap-1.5 px-4 pt-3 text-xs text-fuchsia-600 dark:text-fuchsia-400 font-semibold">
          <Rocket className="h-3 w-3" /> In evidenza fino a {formatDistanceToNow(new Date(post.boost_until), { addSuffix: true, locale: it })}
        </div>
      )}
      {/* Task #1662 — Milestone special header */}
      {post.post_type === 'milestone' && post.milestone_source && (
        <div className="flex items-center gap-2 px-4 pt-3">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold shadow-md">
            <Sparkles className="h-3 w-3" />
            {(post.milestone_source.kind || 'manual').replace(/_/g, ' ').toUpperCase()}
          </div>
        </div>
      )}

      <div className="p-4 pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <Avatar className="h-11 w-11 ring-2 ring-white dark:ring-gray-800 shadow-sm">
              <AvatarImage src={post.author_avatar_url ?? undefined} />
              <AvatarFallback className={`bg-gradient-to-br ${getAvatarGradient(authorName)} text-white font-semibold text-sm`}>
                {authorInitials}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span id={`post-${post.id}-author`} className="font-semibold text-sm">{authorName}</span>
                {post.post_type !== 'message' && (
                  <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold", config.badgeColor)}>
                    <config.icon className="h-3 w-3" />
                    {config.label}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: it })}
                </span>
                {post.is_edited && <span className="text-[10px] text-gray-400"> · modificato</span>}
                {post.target_type === 'global' && (
                  <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                    · <Eye className="h-2.5 w-2.5" /> Tutti
                  </span>
                )}
              </div>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-0 group-hover/card:opacity-100 transition-opacity">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => onCreateTask(post.id)}>
                <CheckSquare className="h-4 w-4 mr-2 text-blue-500" /> Crea Task
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onCreateAppointment(post.id)}>
                <Calendar className="h-4 w-4 mr-2 text-green-500" /> Crea Appuntamento
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onCreateVideocall(post.id)}>
                <Video className="h-4 w-4 mr-2 text-purple-500" /> Avvia Videochiamata
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {isAdmin && (
                <DropdownMenuItem onClick={() => onPin(post.id, !post.is_pinned)}>
                  <Pin className="h-4 w-4 mr-2 text-amber-500" /> {post.is_pinned ? 'Rimuovi fissaggio' : 'Fissa post'}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onFollow(post.id, !post.is_following)}>
                {post.is_following ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                {post.is_following ? 'Non seguire' : 'Segui'}
              </DropdownMenuItem>
              {!isAuthor && onReport && (
                <DropdownMenuItem onClick={() => onReport(post)} className="text-orange-600 focus:text-orange-600">
                  <Flag className="h-4 w-4 mr-2" /> Segnala
                </DropdownMenuItem>
              )}
              {(isAuthor || isAdmin) && (
                <>
                  <DropdownMenuSeparator />
                  {isAuthor && (
                    <DropdownMenuItem onClick={() => onEdit(post)}>
                      <Edit className="h-4 w-4 mr-2" /> Modifica
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => onDelete(post.id)} className="text-red-600 focus:text-red-600">
                    <Trash2 className="h-4 w-4 mr-2" /> Elimina
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {post.title && (
          <h3 className="text-base font-bold mt-3 leading-snug">{post.title}</h3>
        )}

        {post.content_html && (
          <div
            className="mt-3 prose prose-sm dark:prose-invert max-w-none [&_table]:border-collapse [&_td]:border [&_td]:p-2 [&_th]:border [&_th]:p-2 [&_img]:rounded-lg [&_a]:text-blue-600"
            dangerouslySetInnerHTML={{ __html: post.content_html }}
          />
        )}

        {Array.isArray(post.entity_mentions) && post.entity_mentions.length > 0 && (
          <div className="mt-3 flex flex-col gap-2">
            {post.entity_mentions.map((em: { kind: string; id: string }, i: number) => (
              <EntityCard
                key={`pem-${i}-${em.id}`}
                kind={em.kind as 'lead' | 'deal' | 'contact' | 'task' | 'event' | 'file'}
                id={em.id}
              />
            ))}
          </div>
        )}

        {post.post_type === 'poll' && post.poll && (
          <PollCard
            poll={{
              id: post.poll.id,
              question: post.poll.question,
              is_multiple_choice: post.poll.is_multiple_choice ?? false,
              is_anonymous: post.poll.is_anonymous ?? false,
              closes_at: post.poll.closes_at ?? null,
              options: post.poll.options,
              total_votes: post.poll.total_votes,
              user_votes: post.poll.user_votes,
            }}
            onVote={(optionId) => onVotePoll(post.poll!.id, optionId)}
          />
        )}

        {post.post_type === 'announcement' && (
          <div className="mt-3 flex items-center gap-2 text-xs px-3 py-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200/50 dark:border-amber-800/50">
            <Eye className="h-3.5 w-3.5 text-amber-600" />
            <span className="text-amber-700 dark:text-amber-400">
              Letto da <strong>{post.read_count || 0}</strong> {post.total_recipients ? `su ${post.total_recipients}` : ''} persone
            </span>
            {!post.is_read && (
              <Button variant="ghost" size="sm" className="h-6 text-xs ml-auto text-amber-600 hover:text-amber-700 hover:bg-amber-100" onClick={() => onMarkRead(post.id)}>
                Segna come letto
              </Button>
            )}
          </div>
        )}

        {post.post_type === 'appreciation' && post.appreciation && (
          <div className="mt-3 p-4 bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20 rounded-xl text-center border border-pink-100 dark:border-pink-800/50">
            <div className="text-3xl mb-2">
              {FEED_REACTION_KIND_EMOJI.celebrate}
            </div>
            <Award className="h-8 w-8 mx-auto text-pink-500 mb-2" />
            <p className="text-sm font-semibold text-pink-700 dark:text-pink-300">
              {post.appreciation.badge_type} per {post.appreciation.recipient_first_name} {post.appreciation.recipient_last_name}
            </p>
            {post.appreciation.message && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 italic">"{post.appreciation.message}"</p>
            )}
          </div>
        )}

        {post.attachments && post.attachments.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {post.attachments.map((att: FeedAttachment) => (
              <a
                key={att.id}
                href={att.url || att.file_url || `/api/messenger/feed/attachments/${att.id}/download`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs border border-gray-100 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              >
                <Paperclip className="h-3.5 w-3.5 text-gray-400" />
                <span className="max-w-[200px] truncate font-medium">{att.filename}</span>
              </a>
            ))}
          </div>
        )}

        {post.topics && post.topics.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {post.topics.map((t: FeedTopic) => (
              <span
                key={t.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border"
                style={t.color ? { backgroundColor: `${t.color}20`, color: t.color, borderColor: `${t.color}40` } : undefined}
              >
                <Tag className="h-3 w-3" />{t.name}
              </span>
            ))}
          </div>
        )}

        {post.hashtags && post.hashtags.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {post.hashtags.map((tag: string) => (
              <span key={tag} className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full text-[11px] font-medium cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                <Hash className="h-3 w-3" />{tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Task #1662 — RSVP UI per post di tipo evento */}
      {post.post_type === 'event' && onRsvp && (
        <div className="border-t px-4 py-2.5 bg-blue-50/30 dark:bg-blue-950/10">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide">Partecipi?</span>
            {(['yes', 'maybe', 'no'] as FeedRsvpStatus[]).map(s => {
              const isMine = rsvpSummary?.my_status === s;
              const count = rsvpSummary?.[s] ?? 0;
              const label = s === 'yes' ? 'Sì' : s === 'maybe' ? 'Forse' : 'No';
              return (
                <button
                  key={s}
                  onClick={() => onRsvp(post.id, s)}
                  className={cn(
                    "px-2.5 py-1 rounded-full text-xs font-medium transition-all border",
                    isMine
                      ? "bg-blue-600 text-white border-blue-700 shadow-sm"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-blue-300"
                  )}
                >
                  {label}{count > 0 && <span className="ml-1 opacity-80">· {count}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="border-t px-4 py-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-0.5 flex-wrap">
            {QUICK_REACTIONS_V2.map(({ kind, emoji }) => {
              const reaction = reactions.find((r: FeedReaction) => r.reaction_kind === kind || r.emoji === emoji);
              const count = reaction?.count || 0;
              // Task #1665 — micro-bounce su click reazione (whileTap), rispetto reduced-motion.
              return (
                <motion.button
                  key={kind}
                  type="button"
                  onClick={() => onReact(post.id, emoji, kind)}
                  title={kind}
                  aria-label={`Reagisci con ${kind}${count > 0 ? `, ${count} reazioni` : ''}`}
                  aria-pressed={count > 0}
                  whileTap={reduceMotion ? undefined : { scale: 0.86 }}
                  whileHover={reduceMotion ? undefined : { scale: 1.08 }}
                  transition={{ type: 'spring', stiffness: 520, damping: 18 }}
                  className={cn(
                    "flex items-center gap-1 px-2 py-1 rounded-full text-sm",
                    count > 0
                      ? "bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 shadow-sm border border-blue-100 dark:border-blue-800"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800"
                  )}
                >
                  {emoji}
                  {count > 0 && <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">{count}</span>}
                </motion.button>
              );
            })}
          </div>

          <div className="flex items-center gap-1">
            {totalReactions > 0 && (
              <span className="text-[10px] text-gray-400 mr-1">{totalReactions} reaz.</span>
            )}
            {onBookmark && (
              <button
                onClick={() => onBookmark(post.id, !post.is_bookmarked)}
                title={post.is_bookmarked ? 'Rimuovi segnalibro' : 'Aggiungi ai segnalibri'}
                aria-label={post.is_bookmarked ? 'Rimuovi segnalibro' : 'Aggiungi ai segnalibri'}
                aria-pressed={!!post.is_bookmarked}
                className={cn(
                  "flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors",
                  post.is_bookmarked
                    ? "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400"
                    : "text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                )}
              >
                {post.is_bookmarked ? <BookmarkIcon className="h-3.5 w-3.5 fill-current" /> : <BookmarkPlus className="h-3.5 w-3.5" />}
              </button>
            )}
            {isAdmin && onBoost && post.post_type === 'announcement' && !post.boost_until && (
              <button
                onClick={() => onBoost(post.id, 24)}
                title="Boost annuncio 24h"
                aria-label="Metti in evidenza l'annuncio per 24 ore"
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:text-fuchsia-600 hover:bg-fuchsia-50 dark:hover:bg-fuchsia-900/20"
              >
                <Rocket className="h-3.5 w-3.5" />
              </button>
            )}
            {isAdmin && onClearBoost && post.boost_until && new Date(post.boost_until) > new Date() && (
              <button
                onClick={() => onClearBoost(post.id)}
                title="Rimuovi boost"
                aria-label="Rimuovi la messa in evidenza dell'annuncio"
                aria-pressed={true}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-fuchsia-50 text-fuchsia-600 dark:bg-fuchsia-900/30 dark:text-fuchsia-400"
              >
                <Rocket className="h-3.5 w-3.5 fill-current" />
              </button>
            )}
            <button
              onClick={toggleComments}
              aria-label={showComments ? 'Nascondi commenti' : 'Mostra commenti'}
              aria-expanded={showComments}
              aria-controls={`post-${post.id}-comments`}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
                showComments
                  ? "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-800"
              )}
            >
              <MessageSquare className="h-3.5 w-3.5" />
              {(post.comment_count ?? 0) > 0 ? `${post.comment_count} commenti` : post.post_type === 'question' ? 'Rispondi' : 'Commenta'}
            </button>
          </div>
        </div>
      </div>

      {showComments && (
        <div id={`post-${post.id}-comments`} className="border-t px-4 py-3 space-y-3 bg-gray-50/50 dark:bg-gray-800/30">
          {comments && comments.length > 0 && (
            <div className="space-y-3">
              {comments.map((comment: FeedComment) => {
                const commentAuthor = `${comment.author_first_name || ''} ${comment.author_last_name || ''}`.trim();
                const commentInitials = `${(comment.author_first_name || 'U')[0]}${(comment.author_last_name || '')[0] || ''}`;
                const isOfficial = post.official_answer_comment_id === comment.id;
                const canMarkOfficial = post.post_type === 'question' && (isAuthor || isAdmin) && onMarkOfficial;
                return (
                  <div key={comment.id} className={cn("flex gap-2.5", comment.parent_comment_id && "ml-10")}>
                    <Avatar className="h-7 w-7 shrink-0">
                      <AvatarImage src={comment.author_avatar_url ?? undefined} />
                      <AvatarFallback className={`bg-gradient-to-br ${getAvatarGradient(commentAuthor)} text-white text-[10px] font-semibold`}>
                        {commentInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className={cn(
                        "rounded-xl px-3 py-2 border",
                        isOfficial
                          ? "bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-emerald-300 dark:border-emerald-700 ring-1 ring-emerald-300/50"
                          : "bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700"
                      )}>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-semibold">{commentAuthor}</span>
                          {isOfficial && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-[9px] font-bold uppercase tracking-wide">
                              <CheckCircle2 className="h-2.5 w-2.5" /> Ufficiale
                            </span>
                          )}
                        </div>
                        <div className="text-sm prose prose-sm dark:prose-invert max-w-none mt-0.5" dangerouslySetInnerHTML={{ __html: comment.content_html }} />
                      </div>
                      <div className="flex items-center gap-3 mt-1 px-1">
                        <span className="text-[10px] text-gray-400">
                          {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: it })}
                        </span>
                        {comment.is_edited && <span className="text-[10px] text-gray-400">modificato</span>}
                        {canMarkOfficial && !isOfficial && (
                          <button
                            onClick={() => onMarkOfficial!(post.id, comment.id)}
                            className="text-[10px] text-emerald-600 dark:text-emerald-400 hover:underline font-medium"
                          >
                            Segna come risposta ufficiale
                          </button>
                        )}
                        {canMarkOfficial && isOfficial && onUnmarkOfficial && (
                          <button
                            onClick={() => onUnmarkOfficial(post.id)}
                            className="text-[10px] text-gray-500 hover:text-red-500 hover:underline font-medium"
                          >
                            Rimuovi ufficiale
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <CommentEditor onSubmit={handleComment} isSubmitting={isSubmittingComment} />
        </div>
      )}
    </motion.article>
  );
}

// Task #1665 — memoizzato per evitare re-render dell'intero feed quando un
// solo post cambia (es. reazione, commento). L'oggetto post è immutable in
// pratica (rimpiazzato in lista), confronto referenziale è sufficiente.
const MemoPostCard = React.memo(PostCard);
export { MemoPostCard };
